import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tick02Icon, ArrowLeft01Icon } from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import { LogoMark } from '@/components/ui/Logo';
import type { JoinCandidate } from '@/lib/api/teamJoinRequests';
import { cn } from '@/lib/utils';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';

/**
 * Onboarding step 5, JOIN state (spec docs/superpowers/specs/2026-09-10-team-join-requests-design.md).
 *
 * Rendered by the wizard in place of StepInviteTeam when the signup's work
 * email domain matches a team OWNER's domain. Two states, both owned by the
 * wizard: the OFFER (one or more teams, "Request to join", "Start my own
 * workspace") and SENT (the past-tense confirmation with Finish). Copy is
 * verbatim from docs/get-started-pin-step-proposal.html (cards
 * wizard-step-invite-join-offer and -join-sent, lines 401-435). The step
 * sends nothing itself: `onRequest` asks the page to POST, and the wizard
 * flips to SENT once it succeeds, so "Request sent" is never shown before a
 * row exists.
 *
 * With one team the row is plain, as in the mock. With several (a company
 * with more than one team on its domain) the rows become a radio group with
 * the first selected, and the primary requests the chosen team; the copy is
 * unchanged.
 */
export interface StepJoinTeamProps {
  /** The signup's work domain, named in the sub-heading ("People at acme.com already review here."). */
  domain: string;
  /** Owner-matched teams, most members first; at least one. */
  candidates: JoinCandidate[];
  /** The request the wizard already sent (or a candidate the server marked requested): renders the SENT state. */
  requested: { teamId: string; teamName: string } | null;
  isRequesting: boolean;
  /** The server's refusal sentence, rendered inline; the offer stays. */
  requestError: string | null;
  /** Local site: the finishing copy says "Setting up your project..." like StepProject. */
  isLocalEntry?: boolean;
  onRequest: (teamId: string) => void;
  onStartOwn: () => void;
  /** Finish from the SENT state: the wizard saves its own workspace, invites nobody, routes. */
  onFinish: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

function memberCountLabel(n: number): string {
  return `${n} member${n === 1 ? '' : 's'}`;
}

export function StepJoinTeam({
  domain,
  candidates,
  requested,
  isRequesting,
  requestError,
  isLocalEntry = false,
  onRequest,
  onStartOwn,
  onFinish,
  onBack,
  isSubmitting,
}: StepJoinTeamProps) {
  const [selectedTeamId, setSelectedTeamId] = useState(candidates[0]?.teamId ?? '');
  const selected = candidates.find((c) => c.teamId === selectedTeamId) ?? candidates[0];
  const multiple = candidates.length > 1;
  log.info('[StepJoinTeam] render', { domain, candidates: candidates.length, selected: selected?.teamId, requested: requested?.teamId ?? null, isRequesting, isSubmitting });

  // One report per offer, not per render: the wizard re-renders this step on
  // every keystroke elsewhere, and the ref survives StrictMode's double mount.
  const shownRef = useRef(false);
  useEffect(() => {
    if (requested || shownRef.current) return;
    shownRef.current = true;
    capture('onboarding_join_offer_shown', { candidates: candidates.length });
  }, [requested, candidates.length]);

  if (requested) {
    const pendingLabel = isLocalEntry ? 'Setting up your project...' : 'Opening your review...';
    return (
      <div className="space-y-6">
        <div className="text-center">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600" aria-hidden="true">
            <HugeiconsIcon icon={Tick02Icon} size={32} strokeWidth={2} color="currentColor" className="h-8 w-8" />
          </span>
          <h2 className="hero-title">Request sent to {requested.teamName}</h2>
          <p className="mt-2 text-gray-500">
            We&apos;ll email you when the owner approves. Until then, your own workspace is ready.
          </p>
        </div>

        <Button variant="primary" size="md" className="w-full" disabled={isSubmitting} onClick={() => { log.info('[StepJoinTeam] finish clicked'); onFinish(); }}>
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              {pendingLabel}
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              Finish
              <HugeiconsIcon icon={Tick02Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
            </span>
          )}
        </Button>

        <div className="flex items-center justify-start pt-2">
          <Button variant="ghost" size="md" disabled={isSubmitting} onClick={() => { log.info('[StepJoinTeam] back clicked (sent)'); onBack(); }}>
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
              Back
            </span>
          </Button>
        </div>
      </div>
    );
  }

  const busy = isRequesting || isSubmitting;

  const teamRow = (team: JoinCandidate, asRadio: boolean) => {
    const checked = team.teamId === selected?.teamId;
    const body = (
      <>
        <div className="min-w-0 text-left">
          <p className="truncate text-sm font-medium text-gray-900">{team.name}</p>
          <p className="text-xs text-gray-500">{memberCountLabel(team.memberCount)}</p>
        </div>
        <LogoMark className="h-5 w-5 shrink-0" />
      </>
    );
    const rowClass = 'flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3';
    if (!asRadio) {
      return (
        <div key={team.teamId} className={cn(rowClass, 'border-gray-200 bg-white')}>
          {body}
        </div>
      );
    }
    return (
      <button
        key={team.teamId}
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={busy}
        onClick={() => {
          log.info('[StepJoinTeam] team selected', { teamId: team.teamId });
          setSelectedTeamId(team.teamId);
        }}
        className={cn(
          rowClass,
          'focus-glow focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50',
        )}
      >
        {body}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="hero-title">Join your team on OverlayQA?</h2>
        <p className="mt-2 text-gray-500">People at {domain} already review here.</p>
      </div>

      {multiple ? (
        <div className="space-y-2" role="radiogroup" aria-label="Teams you can ask to join">
          {candidates.map((team) => teamRow(team, true))}
        </div>
      ) : (
        <div className="space-y-2">{candidates.map((team) => teamRow(team, false))}</div>
      )}

      <p className="text-xs text-gray-500">We&apos;ll ask the team&apos;s owner. You keep your own workspace until they approve.</p>

      {requestError && (
        <p className="text-sm text-red-600" role="alert">
          {requestError}
        </p>
      )}

      <Button
        variant="primary"
        size="md"
        className="w-full"
        disabled={busy || !selected}
        onClick={() => {
          if (!selected) return;
          log.info('[StepJoinTeam] request clicked', { teamId: selected.teamId });
          onRequest(selected.teamId);
        }}
      >
        {isRequesting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Sending your request...
          </span>
        ) : (
          'Request to join'
        )}
      </Button>

      {/* Two ghost controls on one row, the same shape as the invite step's Back + Skip. */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="ghost" size="md" disabled={busy} onClick={() => { log.info('[StepJoinTeam] back clicked'); onBack(); }}>
          <span className="flex items-center gap-2">
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
            Back
          </span>
        </Button>
        <Button variant="ghost" size="md" disabled={busy} onClick={() => { log.info('[StepJoinTeam] start own clicked'); onStartOwn(); }}>
          Start my own workspace
        </Button>
      </div>
    </div>
  );
}
